import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ApiClientError } from '../api/client';
import type { CreateUserRequest, Role, UpdateUserRequest, User } from '../api/users';
import type { ProjectTile } from '../api/projects';
import { userActionBlock } from '../api/userActions';
import { useProjects } from '../hooks/useProjects';
import { usePageTitle } from '../hooks/usePageTitle';
import { useRefreshToc } from '../hooks/useToc';
import {
  useCreateUser,
  useDeleteUser,
  useDepartments,
  useMe,
  useRoles,
  useUpdateUser,
  useUsers,
} from '../hooks/useUsers';

/**
 * Users page (`/users`, opened from the user menu). Mirrors the structure of Spyglass's `UsersPage`: MUI Paper + Table + IconButton
 * action column + Dialog forms. Permission gate is enforced server-side; this page surfaces a "no
 * permission" message if /api/v1/users returns 403.
 */
export default function UsersPage() {
  usePageTitle('Users');
  const navigate = useNavigate();
  const [showDeleted, setShowDeleted] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<User | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<User | null>(null);

  const { data: me } = useMe();
  const { data: projects } = useProjects();
  const usersQ = useUsers(showDeleted);
  const deleteUser = useDeleteUser();

  const canCreate = me?.permissions.includes('CREATE_USERS') ?? false;
  const canEdit = me?.permissions.includes('EDIT_USERS') ?? false;

  const projectName = (id: string): string => projects?.find((p) => p.id === id)?.name ?? id;

  if (usersQ.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (usersQ.error instanceof ApiClientError && usersQ.error.status === 403) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning" data-testid="admin-users-forbidden">
          You don&apos;t have permission to manage users.
        </Alert>
      </Box>
    );
  }

  if (usersQ.error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity="error"
          data-testid="admin-users-error"
          action={
            <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={() => usersQ.refetch()}>
              Retry
            </Button>
          }
        >
          Error loading users: {usersQ.error.message}
        </Alert>
      </Box>
    );
  }

  const users = usersQ.data ?? [];

  return (
    <Box className="admin-users" sx={{ p: 2 }} data-testid="admin-users-page">
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Users</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                  size="small"
                  slotProps={{ input: { 'data-testid': 'admin-users-show-deleted-input' } as Record<string, unknown> }}
                />
              }
              label="Show Deleted"
            />
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                data-testid="admin-create-user-button"
              >
                Add User
              </Button>
            )}
            <Tooltip title="Refresh">
              <IconButton onClick={() => usersQ.refetch()} data-testid="admin-users-refresh-button">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small" data-testid="admin-users-table">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Projects</TableCell>
                {showDeleted && <TableCell>Status</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => {
                const actionBlock = userActionBlock(me, u);
                const ownerGuarded = actionBlock === 'owner-target';
                const adminLikeRole = u.role === 'OWNER' || u.role === 'ADMIN';
                return (
                  <TableRow key={u.email} data-testid={`admin-user-row-${u.email}`}>
                    <TableCell>{u.email}</TableCell>
                    <TableCell data-testid={`admin-user-role-${u.email}`}>
                      <Chip label={u.role} size="small" />
                    </TableCell>
                    <TableCell>
                      {u.department || (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell data-testid={`admin-user-projects-${u.email}`}>
                      {adminLikeRole ? (
                        <Chip label="(all)" size="small" variant="outlined" />
                      ) : u.subprojects.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {u.subprojects.map((id) => (
                            <Chip key={id} label={projectName(id)} size="small" variant="outlined" />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    {showDeleted && (
                      <TableCell>
                        {u.isDeleted ? (
                          <Chip label="Deleted" color="error" size="small" />
                        ) : (
                          <Chip label="Active" color="success" size="small" />
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <Tooltip title="View history">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/users/${encodeURIComponent(u.email)}/history`)}
                          data-testid={`admin-user-history-button-${u.email}`}
                        >
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                      {canEdit && (
                        <Tooltip title={ownerGuarded ? 'Only an OWNER can edit an OWNER' : 'Edit user'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setEditTarget(u)}
                              disabled={actionBlock !== null}
                              data-testid={`admin-user-edit-button-${u.email}`}
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {canEdit && (
                        <Tooltip title={ownerGuarded ? 'Only an OWNER can delete an OWNER' : 'Delete user'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setPendingDelete(u)}
                              disabled={actionBlock !== null}
                              data-testid={`admin-user-delete-button-${u.email}`}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <WikiMaintenanceSection />

      <DeleteConfirmDialog
        user={pendingDelete}
        onCancel={() => {
          // Clear any leftover mutation error so the next dialog open doesn't show a stale message
          // from an earlier failed attempt on a different user.
          deleteUser.reset();
          setPendingDelete(null);
        }}
        onConfirm={async () => {
          if (!pendingDelete) return;
          try {
            await deleteUser.mutateAsync(pendingDelete.email);
            deleteUser.reset();
            setPendingDelete(null);
          } catch {
            // Error rendered in the dialog.
          }
        }}
        isDeleting={deleteUser.isPending}
        error={deleteUser.error?.message ?? null}
      />

      <CreateUserDialog
        open={createOpen}
        projects={projects ?? []}
        onClose={() => setCreateOpen(false)}
      />

      {editTarget && (
        <EditUserDialog
          key={editTarget.email}
          user={editTarget}
          projects={projects ?? []}
          onClose={() => setEditTarget(null)}
        />
      )}
    </Box>
  );
}

function WikiMaintenanceSection() {
  const refreshToc = useRefreshToc();
  return (
    <Paper sx={{ p: 2, mt: 2 }} data-testid="admin-wiki-maintenance">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h6">Wiki maintenance</Typography>
          <Typography variant="body2" color="text.secondary">
            The product-wiki TOC is cached server-side and refreshed automatically after each ingest. Use this if you
            ran a standalone ingest task or edited files on disk and want the sidebar to pick up the change immediately.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refreshToc.mutate()}
          disabled={refreshToc.isPending}
          data-testid="admin-refresh-toc-button"
        >
          {refreshToc.isPending ? 'Refreshing…' : 'Refresh TOC'}
        </Button>
      </Box>
      {refreshToc.isSuccess && (
        <Alert severity="success" data-testid="admin-refresh-toc-success">
          TOC cache refreshed.
        </Alert>
      )}
      {refreshToc.isError && (
        <Alert severity="error" data-testid="admin-refresh-toc-error">
          {refreshToc.error instanceof Error ? refreshToc.error.message : 'Failed to refresh TOC.'}
        </Alert>
      )}
    </Paper>
  );
}

interface DeleteConfirmProps {
  user: User | null;
  onCancel: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
  error: string | null;
}

function DeleteConfirmDialog({ user, onCancel, onConfirm, isDeleting, error }: DeleteConfirmProps) {
  return (
    <Dialog
      open={user !== null}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { 'data-testid': 'admin-user-delete-dialog' } as Record<string, unknown> }}
    >
      <DialogTitle>Delete user?</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="admin-user-delete-error">
            {error}
          </Alert>
        )}
        <Typography>
          <strong>{user?.email}</strong> will be soft-deleted (history preserved).
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isDeleting} data-testid="admin-user-delete-cancel-button">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={isDeleting}
          data-testid="admin-user-delete-confirm-button"
        >
          {isDeleting ? <CircularProgress size={20} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface CreateUserDialogProps {
  open: boolean;
  projects: ProjectTile[];
  onClose: () => void;
}

function CreateUserDialog({ open, projects, onClose }: CreateUserDialogProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('USER');
  const [department, setDepartment] = useState('');
  const [subprojects, setSubprojects] = useState<string[]>([]);

  const rolesQ = useRoles(open);
  const departmentsQ = useDepartments(open);
  const createUser = useCreateUser();

  const availableRoles = rolesQ.data?.map((r) => r.role) ?? ['USER'];
  const availableDepartments = departmentsQ.data ?? [];
  const subprojectsRelevant = role === 'USER';

  const reset = () => {
    setEmail('');
    setRole('USER');
    setDepartment('');
    setSubprojects([]);
    createUser.reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    const body: CreateUserRequest = {
      email: email.trim(),
      role,
      department: department.trim(),
      subprojects: subprojectsRelevant ? subprojects : [],
    };
    try {
      await createUser.mutateAsync(body);
      handleClose();
    } catch {
      // Error stays in the Alert.
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { 'data-testid': 'admin-create-user-dialog' } as Record<string, unknown> }}
    >
      <DialogTitle>Create User</DialogTitle>
      <DialogContent>
        {createUser.error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="admin-create-user-error">
            {createUser.error.message}
          </Alert>
        )}
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            slotProps={{ htmlInput: { 'data-testid': 'admin-create-user-email-input' } }}
            autoFocus
          />
          <FormControl fullWidth data-testid="admin-create-user-role-select">
            <InputLabel id="create-user-role-label">Role</InputLabel>
            <Select
              labelId="create-user-role-label"
              value={role}
              label="Role"
              onChange={(e) => setRole(e.target.value as Role)}
            >
              {availableRoles.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {availableDepartments.length > 0 ? (
            <FormControl fullWidth data-testid="admin-create-user-department-select">
              <InputLabel id="create-user-department-label">Department</InputLabel>
              <Select
                labelId="create-user-department-label"
                value={department}
                label="Department"
                onChange={(e) => setDepartment(e.target.value)}
              >
                <MenuItem value="">(none)</MenuItem>
                {availableDepartments.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              label="Department"
              fullWidth
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              slotProps={{ htmlInput: { 'data-testid': 'admin-create-user-department-input' } }}
              helperText="(no departments configured)"
            />
          )}
          {subprojectsRelevant && projects.length > 0 && (
            <FormControl fullWidth data-testid="admin-create-user-projects">
              <InputLabel id="create-user-projects-label">Projects</InputLabel>
              <Select
                labelId="create-user-projects-label"
                label="Projects"
                multiple
                value={subprojects}
                onChange={(e) =>
                  setSubprojects(typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]))
                }
                renderValue={(selected) =>
                  (selected as string[]).map((id) => projects.find((p) => p.id === id)?.name ?? id).join(', ')
                }
              >
                {projects.map((p) => (
                  <MenuItem
                    key={p.id}
                    value={p.id}
                    data-testid={`admin-create-user-project-checkbox-${p.id}`}
                  >
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} data-testid="admin-create-user-cancel-button">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!email.trim() || createUser.isPending}
          data-testid="admin-create-user-submit-button"
        >
          {createUser.isPending ? <CircularProgress size={20} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface EditUserDialogProps {
  user: User;
  projects: ProjectTile[];
  onClose: () => void;
}

/**
 * Mount with `key={user.email}` so React remounts the component (and re-initializes state) when
 * the operator picks a different row to edit. Avoids the setState-in-effect smell.
 */
function EditUserDialog({ user, projects, onClose }: EditUserDialogProps) {
  const [role, setRole] = useState<Role>(user.role);
  const [department, setDepartment] = useState(user.department);
  const [subprojects, setSubprojects] = useState<string[]>(
    user.role === 'USER' ? [...user.subprojects] : [],
  );

  const rolesQ = useRoles();
  const departmentsQ = useDepartments();
  const updateUser = useUpdateUser();

  const availableRoles = rolesQ.data?.map((r) => r.role) ?? ['USER'];
  const availableDepartments = departmentsQ.data ?? [];
  const subprojectsRelevant = role === 'USER';

  const handleClose = () => {
    updateUser.reset();
    onClose();
  };

  const handleUpdate = async () => {
    const body: UpdateUserRequest = {
      role,
      department,
      subprojects: subprojectsRelevant ? subprojects : [],
    };
    try {
      await updateUser.mutateAsync({ email: user.email, body });
      handleClose();
    } catch {
      // Error rendered inline.
    }
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { 'data-testid': 'admin-edit-user-dialog' } as Record<string, unknown> }}
    >
      <DialogTitle>Edit User</DialogTitle>
      <DialogContent>
        {updateUser.error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="admin-edit-user-error">
            {updateUser.error.message}
          </Alert>
        )}
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Email" value={user.email} disabled fullWidth />
          <FormControl fullWidth data-testid="admin-edit-user-role-select">
            <InputLabel id="edit-user-role-label">Role</InputLabel>
            <Select
              labelId="edit-user-role-label"
              value={role}
              label="Role"
              onChange={(e) => setRole(e.target.value as Role)}
            >
              {availableRoles.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {availableDepartments.length > 0 ? (
            <FormControl fullWidth data-testid="admin-edit-user-department-select">
              <InputLabel id="edit-user-department-label">Department</InputLabel>
              <Select
                labelId="edit-user-department-label"
                value={department}
                label="Department"
                onChange={(e) => setDepartment(e.target.value)}
              >
                <MenuItem value="">(none)</MenuItem>
                {availableDepartments.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              label="Department"
              fullWidth
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              slotProps={{ htmlInput: { 'data-testid': 'admin-edit-user-department-input' } }}
            />
          )}
          {subprojectsRelevant ? (
            projects.length > 0 && (
              <FormControl fullWidth data-testid="admin-edit-user-projects">
                <InputLabel id="edit-user-projects-label">Projects</InputLabel>
                <Select
                  labelId="edit-user-projects-label"
                  label="Projects"
                  multiple
                  value={subprojects}
                  onChange={(e) =>
                    setSubprojects(typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]))
                  }
                  renderValue={(selected) =>
                    (selected as string[]).map((id) => projects.find((p) => p.id === id)?.name ?? id).join(', ')
                  }
                >
                  {projects.map((p) => (
                    <MenuItem
                      key={p.id}
                      value={p.id}
                      data-testid={`admin-edit-user-project-checkbox-${p.id}`}
                    >
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="admin-edit-user-projects-overlay-note"
            >
              {role}s see every project automatically — no per-project assignment needed.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} data-testid="admin-edit-user-cancel-button">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdate}
          disabled={updateUser.isPending}
          data-testid="admin-edit-user-submit-button"
        >
          {updateUser.isPending ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

